import CheckIcon from '@mui/icons-material/Check';
import { LinkedinTypes } from 'enums';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TmContactApprovalTask, TmMessageApprovalTask } from './task-manager.types';
import { getTaskInitials, TmSectionCard, TmTaskRow } from './tm-section-card';

type ApprovalTask = TmMessageApprovalTask | TmContactApprovalTask;

/** Wartende Freigaben einer Aufgabe: Nachrichten oder (Kontaktfreigabe) Kontakte. */
const pendingOf = (task: ApprovalTask): number =>
  'toBeApprovedContacts' in task ? task.toBeApprovedContacts : task.toBeApprovedMessages;

interface ItemProps {
  task: ApprovalTask;

  handleOpenApproveMessagesDialog(funnelId: string): () => void;
}

const TaskItem: React.FC<ItemProps> = ({ task, handleOpenApproveMessagesDialog }) => {
  const { t } = useTranslation();

  const { funnelId, funnelName, linkedinAccount } = task;

  // Ohne Konto (gelöscht) stand hier „undefined undefined": die Vorlage setzte fehlende
  // Namensteile als Text ein.
  const userName = linkedinAccount
    ? _.compact([linkedinAccount.firstName, linkedinAccount.lastName]).join(' ')
    : t('taskAccountDeleted');

  const productLabel =
    linkedinAccount?.liProductType === LinkedinTypes.LINKEDIN
      ? 'LinkedIn'
      : linkedinAccount?.liProductType === LinkedinTypes.SALES_NAV
        ? 'Sales Navigator'
        : linkedinAccount?.liProductType === LinkedinTypes.RECRUITER
          ? 'Recruiter'
          : '';

  return (
    <TmTaskRow
      initials={getTaskInitials(linkedinAccount?.firstName, linkedinAccount?.lastName, linkedinAccount?.userEmail)}
      name={userName || t('linkedinUser')}
      meta={_.compact([linkedinAccount?.userEmail, productLabel]).join(' · ')}
      context={funnelName || '-'}
      contextLabel={t('campaign')}
      amount={`${pendingOf(task) || '-'}`}
      actionText={t('reviewTask')}
      handleAction={handleOpenApproveMessagesDialog(funnelId)}
    />
  );
};

interface Props {
  tasks: ApprovalTask[];
  total: number | undefined;
  /** `contacts`: Kontakte freigeben statt Nachrichten (gleiche Karte, andere Texte). */
  variant?: 'messages' | 'contacts';

  handleOpenApproveMessagesDialog(funnelId: string): () => void;
}

const TmMessageApproval: React.FC<Props> = ({ tasks, total, variant = 'messages', handleOpenApproveMessagesDialog }) => {
  const { t } = useTranslation();
  const pending = _.sumBy(tasks, (task) => pendingOf(task) || 0);

  return (
    <TmSectionCard
      icon={<CheckIcon fontSize={'small'} />}
      iconBackground={'#EDECFF'}
      iconColor={'#4F44FF'}
      title={variant === 'contacts' ? t('contactsToApprove') : t('messagesToApprove')}
      count={total}
      hint={variant === 'contacts' ? t('contactsToApproveHint', { count: pending }) : t('messagesToApproveHint', { messageCount: pending })}
    >
      {tasks.map((task, index) => (
        <TaskItem key={index} task={task} handleOpenApproveMessagesDialog={handleOpenApproveMessagesDialog} />
      ))}
    </TmSectionCard>
  );
};

export default TmMessageApproval;
